/**
 * Language registry — the single source of truth for which locales the app
 * supports and how each is presented in the switcher.
 *
 *   endonym → the language's own name, shown in the menu so a speaker always
 *             recognises their language regardless of the active locale.
 *   abbr    → the 2-letter label shown in the collapsed pill.
 *   flag    → a SQUARE SVG in /public/flags; the UI clips it to a circle.
 *
 * To add a language: drop a flag SVG in public/flags, add a row here, and add a
 * matching catalog in ./messages (the catalog type turns a missing one into a
 * compile error).
 */
export const LANGUAGES = [
  { code: 'en', endonym: 'English', abbr: 'EN', flag: '/flags/en.svg' },
  { code: 'fr', endonym: 'Français', abbr: 'FR', flag: '/flags/fr.svg' },
  { code: 'es', endonym: 'Español', abbr: 'ES', flag: '/flags/es.svg' },
  { code: 'pt', endonym: 'Português', abbr: 'PT', flag: '/flags/pt.svg' },
  { code: 'nl', endonym: 'Nederlands', abbr: 'NL', flag: '/flags/nl.svg' },
] as const

export type Language = (typeof LANGUAGES)[number]
export type LanguageCode = Language['code']

export const DEFAULT_LANGUAGE: LanguageCode = 'en'

/** localStorage key holding the user's chosen locale. */
export const LOCALE_STORAGE_KEY = 'edf:locale'

const CODES: readonly string[] = LANGUAGES.map((language) => language.code)

export function isLanguageCode(value: string | null | undefined): value is LanguageCode {
  return value != null && CODES.includes(value)
}

export function getLanguage(code: LanguageCode): Language {
  // Safe by construction: `code` is a LanguageCode, so a row always exists.
  return LANGUAGES.find((language) => language.code === code) ?? LANGUAGES[0]
}

/**
 * Initial locale on first paint: a previously stored choice wins; otherwise
 * best-effort match the browser language; otherwise English. Guarded for
 * non-browser environments even though this app is client-only today.
 */
export function getInitialLocale(): LanguageCode {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY)
    if (isLanguageCode(stored)) return stored
  } catch {
    // localStorage can throw (private mode / disabled); fall through.
  }
  const browser = window.navigator.language?.slice(0, 2).toLowerCase()
  if (isLanguageCode(browser)) return browser
  return DEFAULT_LANGUAGE
}
